import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as InformationIcon } from '../../../../../assets/icons/v3/information-icon.svg';
import BdsLoadingFullScreen from '../../../../../shared/components/v3/BdsLoadingFullScreen/BdsLoadingFullScreen';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import EmptyCard from '../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import BdsDivider from '../../../../../shared/components/v3/Globals/BdsDivider/BdsDivider';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { COLLABORATORS_HOME_ROUTE } from '../../../../../shared/constants/auth';
import { getChargeTypeBehavior } from '../../../../../shared/constants/chargeTypes';
import {
  CLICK_ACTION,
  MANAGE_COLLABORATORS_CHECKOUT,
  MANAGE_COLLABORATORS_CHECKOUT_UPSELLING
} from '../../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST, ADD_COLLABORATOR_FAILED_TOAST } from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { ICompanyContract } from '../../../../../shared/interfaces/collaborators';
import { ISubscriptionCheckout, IValidateFileSuccess } from '../../../../../shared/interfaces/manageCollaborators';
import { IToast } from '../../../../../shared/interfaces/toast';
import Companies from '../../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../shared/translations/manageCollaborators';
import { toLocaleNumberFormat, toLocalStorage } from '../../../../../shared/utils/utils';
import UploadCollaboratorsHeadingContainer from '../HeadingContainer/SubscriptionCollaboratorsHeadingContainer';

import './SubscriptionCheckout.scss';

const SubscriptionCheckout = ({ emailsData, selectedPlan, previousStep, uploadMethod }: ISubscriptionCheckout) => {
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [toastData, setToastData] = useState<IToast>();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingAssign, setLoadingAssign] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [licenseData, setLicenseData] = useState<ICompanyContract>();
  const [needUpdatePlan, setNeedUpdatePlan] = useState<boolean>(false);

  const { company } = useContext(CompanyContext);
  const navigate = useNavigate();

  const getContract = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await Companies.getCompanyContract(company.id, selectedPlan.id);
      setLicenseData(response);
    } catch {
      setError(true);
      setOpenToast(true);
      setToastData(GENERIC_ERROR_TOAST);
    } finally {
      setLoading(false);
    }
  }, [company, selectedPlan.id]);

  const subscribe = async (byFile: boolean) => {
    setLoadingAssign(true);
    let localData = '';
    const method = byFile ? 'upload-file' : 'enter-email';

    try {
      if (byFile) {
        if (!emailsData.requestId) return;
        await Companies.subscribeMembersByFile(company.id, emailsData.requestId);
      } else {
        await Companies.subscribeMembers(company.id, selectedPlan.id.toString(), emailsData.emailsToUpload as string[]);
      }

      TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS_CHECKOUT, {
        num_collaborators: emailsData.emailsToUpload.length,
        method
      });

      const emailsToUpload = byFile
        ? (emailsData.emailsToUpload as IValidateFileSuccess[]).map((member) => member.email)
        : (emailsData.emailsToUpload as string[]);

      const json = {
        emails: emailsToUpload,
        openModal: true,
        startDate: licenseData?.startDate,
        type: selectedPlan.type,
        method,
        supportDependents: selectedPlan.supportDependents
      };

      localData = JSON.stringify(json);
      toLocalStorage('subscriptionAdded', localData);

      navigate(COLLABORATORS_HOME_ROUTE);
    } catch {
      setOpenToast(true);
      setToastData(ADD_COLLABORATOR_FAILED_TOAST);
    } finally {
      setLoadingAssign(false);
    }
  };

  useEffect(() => {
    getContract();
  }, [getContract]);

  useEffect(() => {
    if (!licenseData) return;
    const { enforcesLicenseCap } = getChargeTypeBehavior(licenseData.type);
    if (enforcesLicenseCap && emailsData.emailsToUpload.length > licenseData.availableLicenses) {
      setNeedUpdatePlan(true);
    }
  }, [emailsData, licenseData]);

  useEffect(() => {
    if (emailsData.emailsOmmited.length >= 1) {
      setOpenToast(true);
      setToastData({
        title: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.toast.titleOmitedCollaborators,
        description: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.toast.subtleOmitedCollaborators,
        button: true,
        buttonText: '',
        icon: 'alert'
      });
    }
  }, [emailsData, selectedPlan]);

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const handleAction = () => {
    subscribe(uploadMethod === 'upload-file');
  };

  const handleSecondaryActionOfEmptyCard = () => {
    previousStep();
  };

  const handlePrimaryActionOfEmptyCard = () => {
    TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS_CHECKOUT_UPSELLING);
    setOpenToast(true);
    setToastData({
      title: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.emptyState.toast.title,
      description: TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.emptyState.toast.description,
      button: true,
      buttonText: '',
      icon: 'success'
    });
  };

  if (loadingAssign) {
    return (
      <BdsLoadingFullScreen
        show={loadingAssign}
        text={
          <Trans
            i18nKey={TRANSLATION_MANAGE_COLLABORATORS.loadingText}
            components={{
              b: <b></b>,
              br: <br />
            }}
          />
        }
      />
    );
  }

  if (loading) {
    return (
      <div className="skeleton-container flex justify-center items-center">
        <Skeleton className="skeleton w-full h-full" variant="rectangular" animation={false} />
      </div>
    );
  }

  if (error || !licenseData) {
    return (
      <div className="error-card-container flex flex-col justify-center items-center w-full h-full">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={() => getContract()}
          center={true}
          className="error-card flex flex-col justify-center items-center"
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        />
      </div>
    );
  }

  const renderEmptyCard = () => {
    return (
      <EmptyCard
        className="need-update-plan-card flex flex-col justify-start items-star text-start"
        primaryText={t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.emptyState.title)}
        secondaryText={
          <Trans
            i18nKey={TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.emptyState.description}
            values={{ licenses: licenseData.availableLicenses }}
            components={{
              b: <b></b>
            }}
          />
        }
        buttonText={t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.emptyState.buttonContact)}
        action={handlePrimaryActionOfEmptyCard}
        buttonVariant="primary"
        secondaryAction={handleSecondaryActionOfEmptyCard}
        secondaryButtonText={t(
          TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.emptyState.buttonReset
        )}
      />
    );
  };

  const difference = () => {
    if (emailsData.emailsToUpload.length > licenseData.availableLicenses) {
      return emailsData.emailsToUpload.length - licenseData.availableLicenses;
    }
    return 0;
  };

  const licensesNeeded = difference();
  const behavior = getChargeTypeBehavior(licenseData.type);
  const formattedPrice = `${toLocaleNumberFormat(licenseData?.price)} ${licenseData?.currency}`;
  const noPrice = `0 ${licenseData?.currency}`;
  const quotedPrice = behavior.totalCard === 'incremental' ? formattedPrice : noPrice;

  const hasLicenseInformation =
    behavior.showLicensesTitle || behavior.licensesRow !== 'none' || behavior.totalCard !== 'hidden';

  const renderInformationText = () => (behavior.infoKey ? t(behavior.infoKey) : null);

  const renderRow = (title: string, value?: any) => (
    <div className="flex flex-row justify-between items-center w-full">
      <p>{t(title)}</p>
      <span>{value}</span>
    </div>
  );

  const renderLicensesInformation = () => {
    if (behavior.licensesRow === 'cap') {
      return (
        <>
          {renderRow(
            TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.available,
            licenseData?.availableLicenses
          )}
          {renderRow(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.needToAdd, licensesNeeded)}
        </>
      );
    }
    if (behavior.licensesRow === 'counter') {
      return renderRow(
        TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.counterDescription,
        emailsData.emailsToUpload.length
      );
    }
    if (behavior.licensesRow === 'cost-per-user') {
      return renderRow(
        TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.costPerUser,
        licenseData?.price + ' ' + licenseData?.currency
      );
    }
  };

  const renderCheckoutInformation = () => {
    if (behavior.totalCard === 'hidden') return null;
    const total =
      behavior.totalCard === 'incremental'
        ? `${licenseData.price * emailsData.emailsToUpload.length} ${licenseData?.currency}`
        : noPrice;

    return (
      <BdsCardContainer
        borderRadiusSize="radius-m"
        paddingSize="padding-m"
        className="total-checkout flex flex-row items-center justify-start w-full"
      >
        <div className="info flex flex-col justify-start items-start w-full">
          <p>{t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.totalToAdd)}</p>
          <p>{behavior.totalDescriptionKey && t(behavior.totalDescriptionKey, { prize: quotedPrice })}</p>
        </div>
        <span className="flex flex-row justify-end">{total}</span>
      </BdsCardContainer>
    );
  };

  return (
    <div className="upload-collaborator-checkout-container flex flex-col w-full">
      <BdsCardContainer paddingSize="padding-l" borderRadiusSize="radius-l" className="checkout flex flex-col">
        <UploadCollaboratorsHeadingContainer
          emailsCounter={emailsData.emailsToUpload.length}
          planSelected={selectedPlan}
          loading={loading}
        />
        <BdsDivider color="light" size="s" />

        {needUpdatePlan ? (
          renderEmptyCard()
        ) : (
          <div className="checkout-container flex flex-wrap justify-between align-start w-full">
            {hasLicenseInformation && (
              <div className="license-information flex flex-col ">
                <div className="general flex flex-col justify-start items-start w-full">
                  {behavior.showLicensesTitle && (
                    <p className="title">
                      {t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.licenses, {
                        count: licenseData.availableLicenses
                      })}
                    </p>
                  )}
                  {renderLicensesInformation()}
                </div>
                {renderCheckoutInformation()}
              </div>
            )}
            {behavior.infoKey && (
              <BdsCardContainer
                paddingSize="padding-l"
                borderRadiusSize="radius-l"
                className="extra-information flex flex-row justify-start items-start text-start"
              >
                <div className="icon">
                  <InformationIcon />
                </div>
                <p>{renderInformationText()}</p>
              </BdsCardContainer>
            )}
          </div>
        )}
      </BdsCardContainer>
      {!needUpdatePlan && (
        <div className="button flex flex-row justify-end w-full">
          <BdsButton variant="primary" size="l" action={handleAction} disabled={loadingAssign} loading={loadingAssign}>
            {t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.checkout.button)}
          </BdsButton>
        </div>
      )}
      <Toast
        title={t(toastData?.title || '', { users: emailsData.emailsOmmited.length })}
        description={t(toastData?.description || '', { users: emailsData.emailsOmmited.length })}
        icon={toastData?.icon || 'error'}
        open={openToast}
        button={toastData?.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </div>
  );
};

export default SubscriptionCheckout;
